/**
 * Thin wrapper around web storage for auth tokens + user-preference (language). Kept in one
 * place so the storage mechanism can change without touching callers.
 *
 * Tokens live in EITHER localStorage (the "Remember me" choice — persists across browser
 * restarts) or sessionStorage (cleared when the tab/browser closes). Reads check both; writes
 * pick the store from the `remember` flag and clear the other so only one session ever exists.
 */

const ACCESS_TOKEN_KEY = 'fa_sms.accessToken';
const REFRESH_TOKEN_KEY = 'fa_sms.refreshToken';
const LANGUAGE_KEY = 'fa_sms.language';

/** The store currently holding the session — sessionStorage if it has a token, else localStorage. */
function activeStore(): Storage {
  return sessionStorage.getItem(ACCESS_TOKEN_KEY) !== null ? sessionStorage : localStorage;
}

export const tokenStorage = {
  getAccessToken(): string | null {
    return sessionStorage.getItem(ACCESS_TOKEN_KEY) ?? localStorage.getItem(ACCESS_TOKEN_KEY);
  },
  getRefreshToken(): string | null {
    return sessionStorage.getItem(REFRESH_TOKEN_KEY) ?? localStorage.getItem(REFRESH_TOKEN_KEY);
  },
  /**
   * Persist tokens. `remember` chooses durability: `true` → localStorage (stays signed in after
   * the browser closes), `false` → sessionStorage (signed out on close). When omitted (the silent
   * token-refresh path) the tokens are written back to whichever store already holds the session,
   * preserving the user's original "Remember me" choice.
   */
  setTokens(accessToken: string, refreshToken: string, remember?: boolean): void {
    const store = remember === undefined ? activeStore() : remember ? localStorage : sessionStorage;
    const other = store === localStorage ? sessionStorage : localStorage;
    other.removeItem(ACCESS_TOKEN_KEY);
    other.removeItem(REFRESH_TOKEN_KEY);
    store.setItem(ACCESS_TOKEN_KEY, accessToken);
    store.setItem(REFRESH_TOKEN_KEY, refreshToken);
  },
  clear(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  },
};

export const languageStorage = {
  get(): string | null {
    return localStorage.getItem(LANGUAGE_KEY);
  },
  set(lang: string): void {
    localStorage.setItem(LANGUAGE_KEY, lang);
  },
};
